import { Slice } from "@reduxjs/toolkit";
import { produceWithPatches, enablePatches } from "immer";
import { memoize } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";

import { EntityFormState, FormLens, FormLensData } from "@tdp/common/form";

// Enable immer patches so we can use them to create an overlay with only the changed fields rather than cloning the 
// entire object.
enablePatches();


interface FormSliceSelectors<Entity> {
  getEntityFormById: (state: EntityFormState<Entity>, id: string) => FormLensData<Entity>;
}

/**
 * Given a redux slice that uses an entity form adapter, create a hook that creates 'lenses' to a specific form entity in the state. 
 * 
 * This is useful for interacting with the underlying form object in a more ergonomic way.
 * @param slice The form entity slice
 * @returns a hook that returns a form lens for a specific entity's id
 */
export function createFormLensHook<Entity>(slice: Pick<Slice<EntityFormState<Entity>>, 'actions' | 'getSelectors' | 'selectSlice'>): (id: string) => FormLens<Entity> {
  const selectSlice = slice.selectSlice as (state: unknown) => EntityFormState<Entity>;
  const getEntityFormById = (slice.getSelectors() as FormSliceSelectors<Entity>).getEntityFormById;
  const getEntityFormByIdSelector = (id: string) =>
    // need to memoize so the same state doesn't yield a new form lens every time (it is a complex object)
    memoize((state: unknown) => {
      const slice = selectSlice(state);
      return getEntityFormById(slice, id);
    });

  return function useEntityFormLens(id: string): FormLens<Entity> {
    const state = useSelector(getEntityFormByIdSelector(id));
    const dispatch = useDispatch();
    return {
      ...state,
      update: (cb) => {
        //TODO(nibeauli): evaluate the perf of this approach, this seems wasteful
        const [, patches] = produceWithPatches(state.current, cb);
        dispatch(slice.actions.updateForm({ id, value: patches }))
      },
      clear: () => {
        dispatch(slice.actions.clearForm({ id }));
      }
    }
  }
}
